function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function getSocketRect(parentPart, socketRef) {
  if (!parentPart?.sockets?.length || !socketRef) return null;
  const socket = parentPart.sockets.find((s) => s.id === socketRef);
  if (!socket) return null;
  const box = parentPart.localBox || {};
  const sx = num(socket.x, 0);
  const sy = num(socket.y, 0);
  const sz = num(socket.z, 0);
  const sw = num(socket.w, num(box.w, 0));
  const sh = num(socket.h, num(box.h, 0));
  const sd = num(socket.d, num(box.d, 0));
  return { x: sx, y: sy, z: sz, w: sw, h: sh, d: sd, type: socket.type || 'innerContainer' };
}

export function snapToSocket(part, parentPart) {
  if (!part || !parentPart || !part.socketRef) return part;
  const rect = getSocketRect(parentPart, part.socketRef);
  if (!rect) return part;
  const localBox = { ...(part.localBox || {}) };
  // Clamp part dimensions to parent socket dimensions.
  localBox.w = Math.max(0, Math.min(num(localBox.w, 0), rect.w));
  localBox.h = Math.max(0, Math.min(num(localBox.h, 0), rect.h));
  const w = localBox.w;
  const h = localBox.h;
  const maxX = rect.x + Math.max(0, rect.w - w);
  const maxY = rect.y + Math.max(0, rect.h - h);
  localBox.x = Math.min(maxX, Math.max(rect.x, num(localBox.x, rect.x)));
  localBox.y = Math.min(maxY, Math.max(rect.y, num(localBox.y, rect.y)));

  // Z axis behavior:
  // - frontPlane is fixed on the front face (for doors/facades)
  // - rearPlane is fixed on the back face
  // - innerContainer allows free movement along depth within bounds
  const d = Math.max(0, num(localBox.d, 0));
  const parentDepth = Math.max(0, num(parentPart.localBox?.d, rect.d));
  if (part.socketRef === 'frontPlane' || rect.type === 'frontPlane') {
    localBox.z = Math.max(0, parentDepth - d);
  } else if (part.socketRef === 'rearPlane' || rect.type === 'rearPlane') {
    localBox.z = 0;
  } else {
    const z0 = rect.z;
    const zMax = rect.z + Math.max(0, rect.d - d);
    localBox.z = Math.min(zMax, Math.max(z0, num(localBox.z, z0)));
  }
  return { ...part, localBox };
}

export function checkBounds(part, parentPart) {
  if (!part?.socketRef || !parentPart) return { ok: true };
  const rect = getSocketRect(parentPart, part.socketRef);
  if (!rect) return { ok: true };
  const box = part.localBox || {};
  const x = num(box.x, 0);
  const y = num(box.y, 0);
  const z = num(box.z, 0);
  const w = num(box.w, 0);
  const h = num(box.h, 0);
  const d = num(box.d, 0);
  const ok = x >= rect.x
    && y >= rect.y
    && z >= rect.z
    && x + w <= rect.x + rect.w
    && y + h <= rect.y + rect.h
    && z + d <= rect.z + rect.d;
  return ok ? { ok: true } : { ok: false, reason: 'out_of_socket_bounds', socketRef: part.socketRef };
}

export function checkSupport(part, parentPart) {
  if (!part || !parentPart) return { ok: true };
  if (part.socketRef === 'wallAnchor') return { ok: true };
  if (!part.constraints?.requiresSupport) return { ok: true };
  const parentBox = parentPart.localBox || {};
  const box = part.localBox || {};
  const parentBottom = num(parentBox.y, 0) + num(parentBox.h, 0);
  const childBottom = num(box.y, 0) + num(box.h, 0);
  const touchesBottom = Math.abs(parentBottom - childBottom) <= 0.001;
  if (touchesBottom) return { ok: true };
  return { ok: false, reason: 'missing_support' };
}
